import React, { useEffect, useMemo, useCallback } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useRouter, useFocusEffect } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import {
  TrendingUp,
  ChevronRight,
  Eye,
  Heart,
  FolderOpen,
  MessageCircle,
  ArrowUpRight,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Users,
  Briefcase,
} from 'lucide-react-native';
import { useSharedDDsForMyStartup } from '@/lib/shared-dd';
import { CommitmentProgress } from '@/components/CommitmentProgress';
import { OnboardingIncompleteBanner } from '@/components/onboarding/OnboardingIncompleteBanner';
import Animated, {
  FadeInDown,
  FadeInUp,
} from 'react-native-reanimated';
import { useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { useScoreHistory, useLatestScoreResult } from '@/lib/supabase/score-queries';
import { useDataRoomStore, computeDealReadiness } from '@/lib/dataroom';
import { useProfileStore } from '@/lib/state/profile-store';
import { useScoreStore } from '@/lib/state/score-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useConversations, mapConversationRow, useAuth, useRealtimeNotifications } from '@/lib/supabase';
import { asAuthUserId } from '@/lib/ids';
import { useShallow } from 'zustand/react/shallow';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { TierGate, UpgradePrompt } from '@/components/subscription';
import { ValuationCard } from '@/components/valuation';
import RecentUpdates from '@/components/RecentUpdates';
import { ModeBadge } from '@/components/ModeIndicator';
import { mode } from '@/lib/config/app-mode';
import { useStartupDashboardStats } from '@/lib/dashboard/use-startup-dashboard-stats';
import { StatCard, ActionItem, ActivityItem } from '@/components/startup/DashboardWidgets';
import { useFlag } from '@/lib/feature-flags';
import { useFounderStartup } from '@/lib/interview/use-founder-startup';
import { StrategicAnalysisEntry } from '@/components/strategic-analysis/StrategicAnalysisEntry';
import { UnclaimedSubmissionsCard } from '@/components/startup/UnclaimedSubmissionsCard';
import { useStrategicEntryState } from '@/lib/strategic-analysis/use-strategic-analysis';
import { IRSScoreCard } from '@/components/score';
import {
  scoreToLabel,
  evidenceLevelToSource,
  evidenceSourceMixFlavor,
  type EvidenceLabel,
} from '@/lib/scoring/score-display';
import { APP_LOCALE } from '@/lib/format';

// Default score stats (score pipeline is a separate concern)
const DEFAULT_SCORE_TREND = 5;
const DEFAULT_WEEKLY_SCORES = [62, 65, 63, 68, 71, 72, 74];

const mockRecentActivity = [
  {
    id: '1',
    type: 'view' as const,
    investorName: 'Sarah Chen',
    investorFirm: 'Horizon Ventures',
    action: 'viewed your pitch deck',
    time: '2h ago',
    isOnline: true,
  },
  {
    id: '2',
    type: 'interest' as const,
    investorName: 'Marcus Williams',
    investorFirm: 'Green Capital',
    action: 'expressed interest',
    time: '5h ago',
    isOnline: false,
  },
  {
    id: '3',
    type: 'dataroom' as const,
    investorName: 'Elena Rodriguez',
    investorFirm: 'FinTech Angels',
    action: 'requested data room access',
    time: '1d ago',
    isOnline: true,
  },
];

export default function StartupDashboard() {
  useTrackScreen('StartupDashboard');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // Strategic analysis, as a dashboard section. The SAME card the company
  // profile shows — one state machine, one permission gate, one set of
  // founder-private rules. A second, dashboard-shaped rendering of the same
  // states is how the two drift apart.
  const { startupId: strategicStartupId } = useFounderStartup();
  const strategicAnalysisEnabled = useFlag('strategic_analyses', { default: false });
  const strategicEntry = useStrategicEntryState(strategicStartupId ?? undefined, {
    canEdit: true,
    enabled: strategicAnalysisEnabled,
  });
  // Score store — shallow selectors for non-primitives
  const scoreCriteria = useScoreStore(useShallow((s) => s.scoreCriteria));
  const aiRecommendations = useScoreStore(useShallow((s) => s.aiRecommendations));

  // Profile store — shallow selectors for non-primitives
  const startupProfile = useProfileStore(useShallow((s) => s.startupProfile));
  const user = useProfileStore(useShallow((s) => s.user));
  const verificationRequests = useProfileStore(useShallow((s) => s.verificationRequests));

  // Dealflow store — subscribe to the underlying arrays so the dashboard re-renders
  // when commitments are seeded after mount; getCommitmentSummary is a stable fn ref
  // and would not trigger a re-render on its own.
  const getCommitmentSummary = useDealflowStore((s) => s.getCommitmentSummary);
  const commitmentsCount = useDealflowStore((s) => s.commitments.length);
  const externalCommitmentsCount = useDealflowStore((s) => s.externalCommitments.length);

  // Unread messages — sum of startup-side unread from real conversations.
  const { data: conversationRows } = useConversations(startupProfile?.id, 'startup');

  // Shared DD store
  // Investors currently running DD on this startup. Founders grant data-room
  // access, so they're entitled to know who is diligencing them (product decision
  // 2026-06-15). RLS exposes only the lead investor + status/dates, not the
  // investors' private notes/conclusions.
  const { data: mySharedDDs = [] } = useSharedDDsForMyStartup(startupProfile?.id);
  // DD reports investors have shared with this startup (Phase D, read-only).

  // Pending data-room access requests count (mock-only for now)
  const pendingAccessRequestsCount = useMemo(() => {
    if (!mode.showSampleData || !startupProfile?.id) return 0;
    const { getMockAccessRequests } = require('@/lib/state/mock-data-provider');
    return getMockAccessRequests().filter(
      (r: { startupId: string; status: string }) =>
        r.startupId === startupProfile.id && r.status === 'pending',
    ).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mock data is static per session
  }, [startupProfile?.id]);

  // Auto-load mock data when in mock mode with empty stores
  const setScoreCriteria = useScoreStore((s) => s.setScoreCriteria);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const setUser = useProfileStore((s) => s.setUser);
  const setCommitments = useDealflowStore((s) => s.setCommitments);
  const setExternalCommitments = useDealflowStore((s) => s.setExternalCommitments);
  useEffect(() => {
    if (!mode.showSampleData) return;
    if (scoreCriteria.length === 0) {
      // Synchronous import to ensure data is set before next render
      const { getSampleScoredCriteria } = require('@/lib/state/mock-data');
      setScoreCriteria(getSampleScoredCriteria());
    }
    if (!startupProfile) {
      const { getMockStartups } = require('@/lib/state/mock-data-provider');
      const mockStartup = getMockStartups()[0];
      if (mockStartup) setStartupProfile(mockStartup);
    }
    if (!user) {
      // Sample-data seed: not a real auth id, asserted so the demo path runs
      // through the same typed code as production.
      setUser({ id: asAuthUserId('mock-user'), name: 'Maude Girod', email: 'maude@techflow.ai' });
    }
    // Seed mock commitments so the Fundraising Progress card has data.
    // Persisted dealflow store may be empty if rehydrated from an old session.
    const dealflowState = useDealflowStore.getState();
    if (dealflowState.commitments.length === 0) {
      const { getMockCommitments, getMockExternalCommitments } = require('@/lib/state/mock-data-provider');
      setCommitments(getMockCommitments());
      setExternalCommitments(getMockExternalCommitments());
    }
  }, []);

  // Fetch real dashboard stats from edge function (mock in mock mode)
  // In mock mode without a user, pass a placeholder ID so the query runs
  const statsUserId = user?.id ?? (mode.showSampleData ? 'mock-user' : undefined);
  const { data: statsData } = useStartupDashboardStats(statsUserId);

  // Fetch real score history for trend & sparkline (disabled in mock mode)
  const scoreHistoryId = startupProfile?.id ?? '';
  const { data: scoreHistoryData, refetch: refetchScoreHistory } = useScoreHistory(scoreHistoryId);
  // Latest real engine score — drives the headline independently of whether the
  // Score tab has been visited (the shared scoreCriteria store is populated
  // lazily there). No real score → honest empty headline, never a heuristic.
  const { data: latestScoreData, refetch: refetchLatestScore } = useLatestScoreResult(scoreHistoryId);

  // Refetch the score whenever Home regains focus. The Home tab stays MOUNTED, so
  // a score produced elsewhere (e.g. finishing the scoring interview) wouldn't
  // otherwise appear until a manual reload/Score-tab visit — the headline read a
  // cached "no score". Re-running on focus makes the new score show on return
  // (Adi 2026-07-23 — closes the deferred dashboard-score-refresh gap).
  useFocusEffect(
    useCallback(() => {
      if (mode.showSampleData || !scoreHistoryId) return;
      refetchLatestScore();
      refetchScoreHistory();
    }, [scoreHistoryId, refetchLatestScore, refetchScoreHistory]),
  );

  // Compute weekly scores and trend from real score history.
  // Sample data only in demo/mock mode; in production an empty history shows an
  // honest empty trend — never the DEFAULT demo chart.
  const { weeklyScores, scoreTrend } = useMemo(() => {
    if (mode.showSampleData) {
      return { weeklyScores: DEFAULT_WEEKLY_SCORES, scoreTrend: DEFAULT_SCORE_TREND };
    }
    if (!scoreHistoryData || scoreHistoryData.length === 0) {
      return { weeklyScores: [] as number[], scoreTrend: 0 };
    }
    const recent = scoreHistoryData.slice(-7).map((entry) => Math.round(entry.composite.finalScore));
    const trend = recent.length >= 2 ? recent[recent.length - 1] - recent[0] : 0;
    return { weeklyScores: recent, scoreTrend: trend };
  }, [scoreHistoryData]);

  // Real deal-readiness from the startup's own data-room checklist uploads.
  // Sample numbers only in demo/mock mode (the principle: real-by-default, mock
  // strictly mode-gated). Replaces the previously ungated hardcoded 67/8/12.
  const checklistUploads = useDataRoomStore((s) => s.uploads);
  const checklistStage = useDataRoomStore((s) => s.currentStage);
  const realDealReadiness = useMemo(
    () => computeDealReadiness(checklistUploads, checklistStage),
    [checklistUploads, checklistStage],
  );

  // Sample-only, and honestly so: there is no activity feed to read. The
  // dashboard-stats edge function returns counts (profileViews and friends),
  // not events, and nothing else records "investor X viewed your deck". The
  // section below hides itself when this is empty rather than rendering a
  // heading over nothing — a silent empty list reads as "no interest yet",
  // which is a claim we cannot make.
  const recentActivity = mode.showSampleData ? mockRecentActivity : [];
  const updates = startupProfile?.updates || [];

  // Subscription feature checks
  const hasMessaging = useSubscriptionStore((s) => s.hasFeature('direct_messaging'));

  // Calculate overall score. Prefer the real engine composite; fall back to the
  // shared scoreCriteria store (populated in mock mode, or after a Score-tab
  // visit). Unscored production company → 0 → headline hides.
  const overallScore = useMemo(() => {
    if (latestScoreData) return Math.round(latestScoreData.composite.finalScore);
    if (scoreCriteria.length === 0) return 0;
    const total = scoreCriteria.reduce((sum, c) => sum + c.score, 0);
    const maxTotal = scoreCriteria.reduce((sum, c) => sum + c.maxScore, 0);
    return maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
  }, [latestScoreData, scoreCriteria]);

  // Confidence Score — weighted average of v3 evidence tier multipliers
  // verified = 1.0x, partially-verified = 0.75x, unverified/other = 0.5x
  const confidenceScore = useMemo(() => {
    if (scoreCriteria.length === 0) return 0;
    const evidenceMultiplier = (level: string): number => {
      if (level === 'verified') return 1.0;
      if (level === 'partially-verified') return 0.75;
      return 0.5; // unverified, missing, or legacy values
    };
    const totalConfidence = scoreCriteria.reduce(
      (sum, c) => sum + evidenceMultiplier(c.evidenceLevel),
      0,
    );
    return Math.round((totalConfidence / scoreCriteria.length) * 100);
  }, [scoreCriteria]);

  // Risk Summary
  const riskSummary = useMemo(() => {
    const high = scoreCriteria.filter((c) => c.riskLevel === 'high').length;
    const medium = scoreCriteria.filter((c) => c.riskLevel === 'medium').length;
    const low = scoreCriteria.filter((c) => c.riskLevel === 'low').length;
    return { high, medium, low };
  }, [scoreCriteria]);

  // Derived score-card values for the unified hero
  const previousScore = overallScore - scoreTrend;
  const ventureLabel = overallScore > 0 ? scoreToLabel(overallScore) : undefined;
  const evidenceLabel: EvidenceLabel | undefined =
    confidenceScore >= 80 ? 'High' :
    confidenceScore >= 60 ? 'Moderate' :
    confidenceScore > 0 ? 'Low' : undefined;

  // Source-mix flavor for the scorecard (e.g. "mostly verified")
  const sourceFlavor = useMemo(() => {
    if (overallScore === 0 || scoreCriteria.length === 0) return undefined;
    const sources = scoreCriteria.map((c) => evidenceLevelToSource(c.evidenceLevel, !!c.feedback));
    return evidenceSourceMixFlavor(sources);
  }, [overallScore, scoreCriteria]);

  // Active DDs — investors currently doing due diligence on this startup
  const activeDDs = useMemo(
    () => mySharedDDs.filter((dd) => dd.status === 'active'),
    [mySharedDDs],
  );
  // Completed DDs — closed sessions (drives the "Completed" count on the DD card)
  const closedDDs = useMemo(
    () => mySharedDDs.filter((dd) => dd.status === 'closed'),
    [mySharedDDs],
  );

  // Data Room completeness — mock for now (TODO: wire to real checklist when available)
  // Real data-room completeness = % of the checklist present (was mis-wired to
  // profileCompleteness in production). Sample value only in demo/mock mode.
  const dataRoomCompleteness = mode.showSampleData ? 78 : realDealReadiness.percent;

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Greeting first name — capitalize it so an email-derived/lowercased name
  // ("adi") renders as "Adi". Split on space OR "@" so a raw email never leaks
  // into the greeting (Adi 2026-07-23).
  const rawFirstName = user?.name?.split(/[\s@]/)[0] || '';
  const userName = rawFirstName
    ? rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1)
    : 'Founder';
  const companyName = startupProfile?.name || 'Your Startup';

  // Get top recommendation
  const topRecommendation =
    aiRecommendations.find((r) => r.priority === 'high') || aiRecommendations[0];

  // Pending verifications count
  const pendingVerificationsCount = verificationRequests.filter(
    (r) => r.status === 'pending'
  ).length;

  // Unread messages count — mapped from real conversation rows (startup side).
  const unreadMessagesCount = useMemo(
    () =>
      (conversationRows ?? [])
        .map((row) => mapConversationRow(row))
        .reduce((sum, c) => sum + c.unreadCount, 0),
    [conversationRows]
  );

  // The bell's badge must describe the screen the bell OPENS.
  //
  // It used to light up on `unreadMessagesCount > 0 || pendingVerificationsCount > 0`
  // — unread chat messages and pending verification requests — while the button
  // navigates to /notifications, which shows neither. So an unread message put
  // a red dot on the bell, and tapping it led to a legitimately empty list that
  // could not clear the dot. Messages have their own tab with its own count;
  // this one counts notifications.
  const { user: authUser } = useAuth();
  const { unreadCount } = useRealtimeNotifications(authUser?.id);

  // Aggregate dashboard stats from edge function + local stores
  const dashboardStats = {
    profileViews: statsData?.profileViews ?? 0,
    profileViewsTrend: statsData?.profileViewsTrend ?? 0,
    interestReceived: statsData?.interestedInvestors ?? 0,
    interestReceivedTrend: statsData?.interestedInvestorsTrend ?? 0,
    profileCompleteness: statsData?.profileCompleteness ?? 0,
    scoreTrend,
    unreadMessages: unreadMessagesCount,
    pendingVerifications: pendingVerificationsCount,
    dataRoomViews: statsData?.dataRoomViews ?? 0,
    weeklyScores,
  };

  // Commitment summary for fundraising progress (recomputed when arrays change)
  const commitmentSummary = useMemo(
    () => (startupProfile ? getCommitmentSummary(startupProfile.id, startupProfile) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- length proxies the arrays
    [startupProfile, commitmentsCount, externalCommitmentsCount],
  );

  return (
    <AppLayout title="Dashboard" showSearch>
      <View style={{ flex: 1, backgroundColor: colors.surface.secondary }}>
        {/* PINNED — same reasoning as the investor dashboard. It also fixes the
           grouping defect: GlobalActions was dropped straight into a
           `justify-between` row, so its two buttons became separate flex
           children and the bell was distributed into the MIDDLE of the header
           instead of sitting beside the avatar. The cluster now owns its own
           grouping (see ScreenFrame.GlobalActions). No bottomEdge: tab screen. */}
        <ScreenFrame actions={<ScreenFrame.GlobalActions />}>
          <ScrollView
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 120 }}
          >
            <OnboardingIncompleteBanner />
            {/* Header */}
            <Animated.View
              entering={FadeInDown.delay(50).duration(500)}
              style={{ paddingHorizontal: 20, paddingTop: 20, paddingBottom: 8 }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Text
                    className="text-sm font-medium"
                    style={{
                      color: colors.text.tertiary,
                      letterSpacing: 0.5,
                    }}
                  >
                    {getGreeting()}
                  </Text>
                  <Text
                    className="text-2xl font-bold"
                    numberOfLines={1}
                    style={{
                      color: colors.text.primary,
                      marginTop: 4,
                      letterSpacing: -0.5,
                    }}
                  >
                    {userName}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 6 }}>
                    <View
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: colors.success,
                      }}
                    />
                    <Text
                      className="text-xs font-medium"
                      numberOfLines={1}
                      style={{
                        color: colors.text.secondary,
                        flexShrink: 1,
                      }}
                    >
                      {companyName}
                    </Text>
                    <ModeBadge />
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* Score Hero Card — unified IRSScoreCard blueprint */}
            <Animated.View
              entering={FadeInUp.delay(150).duration(600)}
              style={{ marginHorizontal: 20, marginTop: 20 }}
            >
              <AnimatedPressable
                onPress={() => {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                  router.push('/(startup)/score');
                }}
              >
                {/* Inner wrapper carries the shadow so the View Score Breakdown link sits
                    outside the card's visible bounds (avoids a second halo/border on web). */}
                <View
                  style={{
                    borderRadius: 16,
                    shadowColor: '#000',
                    shadowOpacity: 0.08,
                    shadowRadius: 16,
                    shadowOffset: { width: 0, height: 4 },
                    elevation: 4,
                  }}
                >
                <IRSScoreCard
                  companyName={startupProfile?.name}
                  tagline={startupProfile?.tagline}
                  description={startupProfile?.about}
                  finalScore={overallScore > 0 ? overallScore : undefined}
                  ventureLabel={ventureLabel}
                  ventureScore={overallScore > 0 ? overallScore : undefined}
                  evidenceLabel={evidenceLabel}
                  evidenceConfidence={overallScore > 0 ? confidenceScore : undefined}
                  evidenceSourceFlavor={overallScore > 0 ? sourceFlavor : undefined}
                  dealReadinessPercent={overallScore > 0 ? (mode.showSampleData ? 67 : realDealReadiness.percent) : undefined}
                  dealReadinessPresent={overallScore > 0 ? (mode.showSampleData ? 8 : realDealReadiness.present) : undefined}
                  dealReadinessTotal={overallScore > 0 ? (mode.showSampleData ? 12 : realDealReadiness.total) : undefined}
                  riskSummary={riskSummary}
                  previousScore={overallScore > 0 ? previousScore : undefined}
                  weeklyScores={dashboardStats.weeklyScores}
                  trendUnit="week"
                  onUploadDeck={() => router.push('/(onboarding)/pitch-deck-upload')}
                  onConnectDataRoom={() => router.push('/(onboarding)/data-room-connect')}
                  onScoringInterview={() => router.push('/(startup)/interview')}
                />
                </View>
                {overallScore > 0 && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 10,
                      gap: 6,
                    }}
                  >
                    <BarChart3 size={13} color={colors.primary[500]} />
                    <Text
                      style={{
                        color: colors.primary[500],
                        fontSize: 12,
                        fontWeight: '700',
                      }}
                    >
                      View Score Breakdown
                    </Text>
                    <ChevronRight size={15} color={colors.primary[500]} />
                  </View>
                )}
              </AnimatedPressable>
            </Animated.View>

            {/* Section Divider - Quick Stats */}
            <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
              <SectionDivider label="Quick Stats" marginTop={0} marginBottom={12} />
            </View>

            {/* Quick Stats — passive metrics only (4×1 on web, 2×2 on mobile).
                CTAs (unread messages, pending verifications) live in Priority Actions. */}
            <View style={{ paddingHorizontal: 20, gap: 10 }}>
              {isWeb ? (
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <StatCard
                    icon={Eye}
                    iconBgColor={colors.primary[50]}
                    iconColor={colors.primary[600]}
                    value={dashboardStats.profileViews}
                    label="Profile Views"
                    trend={dashboardStats.profileViewsTrend}
                    trendLabel={`${dashboardStats.profileViewsTrend}% this week`}
                    delay={200}
                  />
                  <StatCard
                    icon={Heart}
                    iconBgColor={colors.traffic.greenLight}
                    iconColor={colors.success}
                    value={dashboardStats.interestReceived}
                    label="Investor Interest"
                    trend={dashboardStats.interestReceivedTrend}
                    trendLabel={`${dashboardStats.interestReceivedTrend} this week`}
                    delay={230}
                  />
                  <StatCard
                    icon={BarChart3}
                    iconBgColor={colors.primary[50]}
                    iconColor={colors.primary[600]}
                    value={`${dashboardStats.profileCompleteness}%`}
                    label="Profile Completion"
                    trendLabel={dashboardStats.profileCompleteness < 100 ? 'Finish to boost' : undefined}
                    delay={260}
                    onPress={() => router.push('/(startup)/company')}
                  />
                  <StatCard
                    icon={FolderOpen}
                    iconBgColor={colors.amber.light}
                    iconColor={colors.warning}
                    value={`${dataRoomCompleteness}%`}
                    label="Data Room Completion"
                    trendLabel={dataRoomCompleteness < 100 ? 'Finish to boost' : undefined}
                    delay={290}
                    onPress={() => router.push('/(startup)/dataroom')}
                  />
                </View>
              ) : (
                <>
                  {/* Mobile Row 1: Momentum */}
                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <StatCard
                      icon={Eye}
                      iconBgColor={colors.primary[50]}
                      iconColor={colors.primary[600]}
                      value={dashboardStats.profileViews}
                      label="Profile Views"
                      trend={dashboardStats.profileViewsTrend}
                      trendLabel={`${dashboardStats.profileViewsTrend}% this week`}
                      delay={200}
                    />
                    <StatCard
                      icon={Heart}
                      iconBgColor={colors.traffic.greenLight}
                      iconColor={colors.success}
                      value={dashboardStats.interestReceived}
                      label="Investor Interest"
                      trend={dashboardStats.interestReceivedTrend}
                      trendLabel={`${dashboardStats.interestReceivedTrend} this week`}
                      delay={230}
                    />
                  </View>
                  {/* Mobile Row 2: Health */}
                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <StatCard
                      icon={BarChart3}
                      iconBgColor={colors.primary[50]}
                      iconColor={colors.primary[600]}
                      value={`${dashboardStats.profileCompleteness}%`}
                      label="Profile Completion"
                      trendLabel={dashboardStats.profileCompleteness < 100 ? 'Finish to boost' : undefined}
                      delay={260}
                      onPress={() => router.push('/(startup)/company')}
                    />
                    <StatCard
                      icon={FolderOpen}
                      iconBgColor={colors.amber.light}
                      iconColor={colors.warning}
                      value={`${dataRoomCompleteness}%`}
                      label="Data Room Completion"
                      trendLabel={dataRoomCompleteness < 100 ? 'Finish to boost' : undefined}
                      delay={290}
                      onPress={() => router.push('/(startup)/dataroom')}
                    />
                  </View>
                </>
              )}
            </View>

            {/* Due Diligence — header/divider always shown; content gated (direct_messaging → "Raise") */}
            <Animated.View
              entering={FadeInUp.delay(380).duration(500)}
              style={{ marginTop: 24 }}
            >
              <View
                className="flex-row items-center"
                style={{ paddingHorizontal: 20, marginBottom: 12, gap: 12 }}
              >
                <Text
                  style={{
                    color: colors.text.primary,
                    fontSize: 12,
                    fontWeight: '700',
                    letterSpacing: 1.4,
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                  numberOfLines={1}
                >
                  Due Diligence
                </Text>
                <View style={{ flex: 1, height: 1, backgroundColor: colors.border.dark }} />
              </View>
              <TierGate
                feature="direct_messaging"
                fallback={
                  <View style={{ paddingHorizontal: 20 }}>
                    <UpgradePrompt
                      targetTier="standard"
                      variant="card"
                      headline="Due Diligence"
                      lockedTitle="Due diligence"
                      maxFeatures={3}
                    />
                  </View>
                }
              >
              <View style={{ paddingHorizontal: 20, gap: 8 }}>
                {/* Running / Completed counts — always shown (0 when none yet); tap → DD hub segment */}
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <AnimatedPressable
                    onPress={() => router.push('/(startup)/shared-dd-reports?filter=running')}
                    style={{ flex: 1, backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, borderRadius: 14, paddingVertical: 14, paddingHorizontal: 16 }}
                  >
                    <Text style={{ color: colors.success, fontSize: 24, fontWeight: '800' }}>{activeDDs.length}</Text>
                    <Text style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '600', letterSpacing: 0.3, textTransform: 'uppercase', marginTop: 2 }} numberOfLines={1}>Running</Text>
                  </AnimatedPressable>
                  <AnimatedPressable
                    onPress={() => router.push('/(startup)/shared-dd-reports?filter=completed')}
                    style={{ flex: 1, backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, borderRadius: 14, paddingVertical: 14, paddingHorizontal: 16 }}
                  >
                    <Text style={{ color: colors.text.secondary, fontSize: 24, fontWeight: '800' }}>{closedDDs.length}</Text>
                    <Text style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '600', letterSpacing: 0.3, textTransform: 'uppercase', marginTop: 2 }} numberOfLines={1}>Completed</Text>
                  </AnimatedPressable>
                </View>
                {/* Reports now live on the dedicated Due Diligence page (Completed segment). */}
                  {activeDDs.slice(0, 3).map((dd) => (
                    <AnimatedPressable
                      key={dd.id}
                      onPress={() => router.push('/(startup)/startup-messages')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                        borderRadius: 14,
                        padding: 14,
                      }}
                    >
                      <View
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          backgroundColor: colors.primary[50],
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Briefcase size={16} color={colors.primary[600]} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            color: colors.text.primary,
                            fontSize: 13,
                            fontWeight: '700',
                          }}
                          numberOfLines={1}
                        >
                          {dd.collaborators.find((c) => c.role !== 'owner')?.investorName ?? dd.collaborators[0]?.investorName ?? 'Investor'}
                        </Text>
                        <Text
                          style={{
                            color: colors.text.tertiary,
                            fontSize: 11,
                            marginTop: 2,
                          }}
                          numberOfLines={1}
                        >
                          Updated {new Date(dd.updatedAt).toLocaleDateString(APP_LOCALE)}
                        </Text>
                      </View>
                      <View
                        style={{
                          backgroundColor: colors.traffic.greenLight,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 999,
                        }}
                      >
                        <Text
                          style={{
                            color: colors.success,
                            fontSize: 10,
                            fontWeight: '700',
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          Active
                        </Text>
                      </View>
                      <ChevronRight size={16} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  ))}
                  {activeDDs.length > 3 && (
                    <AnimatedPressable
                      onPress={() => router.push('/(startup)/verification-requests')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingVertical: 10,
                        gap: 4,
                      }}
                    >
                      <Text
                        style={{
                          color: colors.primary[600],
                          fontSize: 12,
                          fontWeight: '700',
                        }}
                      >
                        View all ({activeDDs.length})
                      </Text>
                      <ChevronRight size={14} color={colors.primary[600]} />
                    </AnimatedPressable>
                  )}
                </View>
              </TierGate>
            </Animated.View>

            {/* Fundraising Progress */}
            {(mode.showSampleData
              ? (commitmentSummary && commitmentSummary.totalCommitted > 0)
              : (startupProfile?.isActivelyRaising && commitmentSummary && commitmentSummary.totalCommitted > 0)) && (
              <Animated.View entering={FadeInUp.delay(320).duration(500)}>
                <AnimatedPressable
                  onPress={() => router.push('/(startup)/company')}
                  style={{ marginHorizontal: 20, marginTop: 16 }}
                >
                  <View
                    style={{
                      padding: 16,
                      borderRadius: 16,
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                      shadowColor: '#000',
                      shadowOpacity: 0.05,
                      shadowRadius: 10,
                      shadowOffset: { width: 0, height: 4 },
                      elevation: 3,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 12,
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <TrendingUp size={16} color={colors.primary[500]} />
                        <Text
                          className="text-sm font-semibold"
                          style={{
                            color: colors.text.primary,
                          }}
                        >
                          Fundraising Progress
                        </Text>
                      </View>
                      <ChevronRight size={16} color={colors.text.tertiary} />
                    </View>
                    {commitmentSummary && (
                      <CommitmentProgress commitmentSummary={commitmentSummary} compact />
                    )}
                  </View>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Section Divider - Priority Actions */}
            <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
              <SectionDivider label="Priority Actions" marginTop={0} marginBottom={12} />
            </View>

            {/* Priority Actions */}
            <Animated.View
              entering={FadeInUp.delay(350).duration(500)}
              style={{
                marginHorizontal: 20,
                backgroundColor: colors.surface.primary,
                borderRadius: 24,
                padding: 20,
                shadowColor: '#000',
                shadowOpacity: 0.05,
                shadowRadius: 10,
                shadowOffset: { width: 0, height: 4 },
                elevation: 2,
              }}
            >
              {(() => {
                const pendingCount =
                  (pendingVerificationsCount > 0 ? 1 : 0) +
                  (unreadMessagesCount > 0 ? 1 : 0) +
                  (pendingAccessRequestsCount > 0 ? 1 : 0) +
                  1;
                if (pendingCount <= 1) return null;
                return (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginBottom: 4,
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: colors.primary[50],
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 10,
                      }}
                    >
                      <Text
                        className="text-xs font-semibold"
                        style={{ color: colors.primary[600] }}
                      >
                        {pendingCount} pending
                      </Text>
                    </View>
                  </View>
                );
              })()}

              {pendingVerificationsCount > 0 && (
                <ActionItem
                  icon={AlertTriangle}
                  iconBgColors={[colors.warning, colors.secondary[600]]}
                  title="Verification Requests"
                  subtitle="Investors waiting for confirmation"
                  badge={pendingVerificationsCount}
                  onPress={() => router.push('/(startup)/verification-requests')}
                  delay={400}
                />
              )}

              {pendingAccessRequestsCount > 0 && (
                <ActionItem
                  icon={Briefcase}
                  iconBgColors={[colors.primary[500], colors.primary[700]]}
                  title="Data Room Access Requests"
                  subtitle="Investors requesting access for due diligence"
                  badge={pendingAccessRequestsCount}
                  onPress={() => router.push('/(startup)/dataroom')}
                  delay={420}
                />
              )}

              {hasMessaging && unreadMessagesCount > 0 && (
                <ActionItem
                  icon={MessageCircle}
                  iconBgColors={[colors.primary[400], colors.primary[600]]}
                  title="Unread Messages"
                  subtitle="From interested investors"
                  badge={unreadMessagesCount}
                  onPress={() => router.push('/(startup)/startup-messages')}
                  delay={450}
                />
              )}

              <ActionItem
                icon={FolderOpen}
                iconBgColors={[colors.warning, '#D97706']}
                title="Complete Data Room"
                subtitle="Upload documents to boost score"
                onPress={() => router.push('/(startup)/dataroom')}
                delay={500}
              />
            </Animated.View>

            {/* Section Divider - Valuation Estimate */}
            <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
              <SectionDivider label="Valuation Estimate" marginTop={0} marginBottom={12} />
            </View>
            <Animated.View entering={FadeInUp.delay(380).duration(500)} style={{ marginHorizontal: 20 }}>
              <ValuationCard />
            </Animated.View>

            {/* Section Divider - Strategic Analysis */}
            {/* An investor has already scored a deck submitted from this
                founder's email, and nobody has claimed it. Renders nothing when
                there is nothing to claim, so it costs an unaffected founder a
                single cached query and no layout. */}
            <UnclaimedSubmissionsCard />

            {strategicAnalysisEnabled && strategicStartupId && (
              <>
                <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
                  <SectionDivider label="Strategic Analysis" marginTop={0} marginBottom={12} />
                </View>
                <Animated.View entering={FadeInUp.delay(400).duration(500)} style={{ marginHorizontal: 20 }}>
                  <StrategicAnalysisEntry
                    canEdit
                    {...strategicEntry}
                    onOpen={() =>
                      router.push(`/(startup)/strategic-analysis?startupId=${strategicStartupId}` as never)}
                    // Blocked goes straight to the focused form, not to a report
                    // screen that cannot show them anything yet.
                    onCompleteInputs={() =>
                      router.push(`/(startup)/strategic-analysis?startupId=${strategicStartupId}&setup=1` as never)}
                  />
                </Animated.View>
              </>
            )}

            {/* Section Divider - AI Insights */}
            {topRecommendation && (
              <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
                <SectionDivider label="Recommendations" marginTop={0} marginBottom={12} />
              </View>
            )}

            {/* AI Recommendation */}
            {topRecommendation && (
              <Animated.View
                entering={FadeInUp.delay(450).duration(500)}
                style={{ marginHorizontal: 20 }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    marginBottom: 12,
                  }}
                >
                  <AnimatedPressable
                    onPress={() => router.push('/(startup)/recommendations')}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
                  >
                    <Text
                      className="text-xs font-semibold"
                      style={{
                        color: colors.primary[500],
                      }}
                    >
                      View All
                    </Text>
                    <ChevronRight size={16} color={colors.primary[500]} />
                  </AnimatedPressable>
                </View>

                <AnimatedPressable
                  onPress={() => {
                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                    router.push('/(startup)/recommendations');
                  }}
                >
                  <LinearGradient
                    colors={[colors.primary[50], `${colors.primary[100]}50`]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={{
                      borderRadius: 24,
                      padding: 20,
                      borderWidth: 1,
                      borderColor: colors.primary[200],
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 12,
                      }}
                    >
                      <View
                        style={{
                          paddingHorizontal: 10,
                          paddingVertical: 4,
                          borderRadius: 8,
                          backgroundColor:
                            topRecommendation.priority === 'high'
                              ? colors.red.light
                              : topRecommendation.priority === 'medium'
                              ? colors.amber.light
                              : colors.traffic.greenLight,
                        }}
                      >
                        <Text
                          className="text-xs font-bold"
                          style={{
                            color:
                              topRecommendation.priority === 'high'
                                ? colors.error
                                : topRecommendation.priority === 'medium'
                                ? colors.warning
                                : colors.success,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {topRecommendation.priority} Priority
                        </Text>
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <ArrowUpRight size={14} color={colors.primary[600]} />
                        <Text
                          className="text-sm font-bold"
                          style={{
                            color: colors.primary[600],
                          }}
                        >
                          +{topRecommendation.potentialScore - topRecommendation.currentScore} pts
                        </Text>
                      </View>
                    </View>

                    <Text
                      className="text-base font-semibold"
                      style={{
                        color: colors.text.primary,
                        marginBottom: 6,
                      }}
                    >
                      {topRecommendation.area}
                    </Text>
                    <Text
                      className="text-sm"
                      style={{
                        color: colors.text.secondary,
                        lineHeight: 20,
                      }}
                    >
                      {topRecommendation.suggestion}
                    </Text>

                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginTop: 16,
                        paddingTop: 14,
                        borderTopWidth: 1,
                        borderTopColor: colors.primary[200],
                        gap: 6,
                      }}
                    >
                      <CheckCircle2 size={14} color={colors.primary[600]} />
                      <Text
                        className="text-xs font-medium"
                        style={{
                          color: colors.primary[600],
                        }}
                      >
                        {topRecommendation.actionItems.length} action items to complete
                      </Text>
                    </View>
                  </LinearGradient>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Section Divider - News & Updates */}
            <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
              <SectionDivider label="News & Updates" marginTop={0} marginBottom={12} />
            </View>

            {/* Recent Updates - Collapsible */}
            <Animated.View
              entering={FadeInUp.delay(500).duration(500)}
              style={{ marginHorizontal: 20, marginBottom: 8 }}
            >
              <RecentUpdates updates={updates} />
            </Animated.View>

            {/* Section Divider - Investor Activity */}
            <View style={{ paddingHorizontal: 20, marginTop: 24 }}>
              <SectionDivider label="Investor Activity" marginTop={0} marginBottom={12} />
            </View>

            {/* Investor Activity - Gated for Standard+ */}
            <TierGate
              feature="view_investor_database"
              fallback={
                <View style={{ marginHorizontal: 20 }}>
                  <UpgradePrompt
                    targetTier="standard"
                    variant="card"
                    headline="See Who's Viewing You"
                    lockedTitle="Who's viewing your startup"
                    maxFeatures={3}
                  />
                </View>
              }
            >
              <Animated.View
                entering={FadeInUp.delay(550).duration(500)}
                style={{
                  marginHorizontal: 20,
                  backgroundColor: colors.surface.primary,
                  borderRadius: 24,
                  padding: 20,
                  shadowColor: '#000',
                  shadowOpacity: 0.05,
                  shadowRadius: 10,
                  shadowOffset: { width: 0, height: 4 },
                  elevation: 2,
                }}
              >
                {recentActivity.length > 0 && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginBottom: 8,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        backgroundColor: colors.traffic.greenLight,
                        paddingHorizontal: 10,
                        paddingVertical: 5,
                        borderRadius: 10,
                      }}
                    >
                      <View
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: colors.success,
                        }}
                      />
                      <Text
                        className="text-xs font-semibold"
                        style={{
                          color: colors.success,
                        }}
                      >
                        {recentActivity.filter((a) => a.isOnline).length} online
                      </Text>
                    </View>
                  </View>
                )}

                {recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ActivityItem activity={activity} delay={600 + index * 80} />
                    {index < recentActivity.length - 1 && (
                      <View
                        style={{
                          height: 1,
                          backgroundColor: colors.border.light,
                          marginLeft: 56,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}

                {/* View All Link */}
                <Pressable
                  onPress={() => router.push('/(startup)/startup-messages')}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 16,
                    paddingTop: 14,
                    borderTopWidth: 1,
                    borderTopColor: colors.border.light,
                    gap: 6,
                  }}
                >
                  <Users size={14} color={colors.primary[500]} />
                  <Text
                    className="text-xs font-semibold"
                    style={{
                      color: colors.primary[500],
                    }}
                  >
                    View All Conversations
                  </Text>
                </Pressable>
              </Animated.View>
            </TierGate>

            <View style={{ height: 32 }} />
          </ScrollView>

        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
